import * as React from 'react';

import { Box, Button, Divider, Popover, Stack, Tab, Tabs, Typography } from '@mui/material';

import { EnumMetadataModel, ExpressionMetadataModel, ParameterMetadata, ParameterValueOption, PrimitiveType } from '../../../../api/api';
import IconComponent from '../../../../common/components/IconComponent';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import ListWithFilter from '../ListWithFilter';
import ConstExpressionInput from './ConstExpressionInput';
import CustomTabPanel from './CustomTabPanel';
import VarExpressionInput from './VarExpressionInput';

type ExpressionPlaceholderPopoverProps = {
	open: boolean;
	anchorEl: HTMLElement | null;
	onClose: () => void;
	isWrapMode: boolean;
	tabValue: number;
	onTabChange: (newValue: number) => void;
	matchingPrimitiveTypes: PrimitiveType[];
	parameterEnum?: EnumMetadataModel;
	valueOptions?: ParameterValueOption[];
	allowCustomValues?: boolean;
	hasLoadedValueOptionsSource?: boolean;
	expressionPending: ExpressionModel | undefined;
	onExpressionPendingChange: (expression: ExpressionModel | undefined) => void;
	variableScope?: IVariableScope;
	parameterMetadata: ParameterMetadata;
	requireCollectionVariable?: boolean;
	matchingExpressionMetadataList: ExpressionMetadataModel[];
	onExpressionSelected: (expressionMetadata: ExpressionMetadataModel) => void;
	onApply: () => void;
};

export default function ExpressionPlaceholderPopover({
	open,
	anchorEl,
	onClose,
	isWrapMode,
	tabValue,
	onTabChange,
	matchingPrimitiveTypes,
	parameterEnum,
	valueOptions,
	allowCustomValues,
	hasLoadedValueOptionsSource,
	expressionPending,
	onExpressionPendingChange,
	variableScope,
	parameterMetadata,
	requireCollectionVariable,
	matchingExpressionMetadataList,
	onExpressionSelected,
	onApply,
}: ExpressionPlaceholderPopoverProps) {
	return (
		<Popover
			open={open}
			transitionDuration={180}
			anchorEl={anchorEl}
			onClose={onClose}
			onClick={(e) => e.stopPropagation()}
			disableRestoreFocus
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'left',
			}}
		>
			<Stack direction="column" spacing={2} padding={2} minHeight={600} maxHeight={650} width={600} sx={{ overflow: 'hidden' }}>
				{isWrapMode && (
					<Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 0, flexShrink: 0 }}>
						Wrap in Expression
					</Typography>
				)}
				<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexGrow: 0, flexShrink: 0, minWidth: 0 }}>
					<Tabs value={tabValue} onChange={(_, newValue) => onTabChange(newValue)} aria-label="basic tabs example" sx={{ flex: '1 1 auto', minWidth: 0 }}>
						<Tab label="Value" value={0} disabled={isWrapMode || matchingPrimitiveTypes.length === 0 || parameterMetadata.expectedExpressionIsAssignable} />
						<Tab label="Variable" value={1} disabled={isWrapMode || !variableScope} />
						<Tab label="Expressions" value={2} disabled={matchingExpressionMetadataList.length === 0 || parameterMetadata.expectedExpressionIsAssignable} />
					</Tabs>
					<Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
						<Divider orientation="vertical" flexItem />
						<Stack direction="row" spacing={1}>
							<Button onClick={onClose}>Cancel</Button>
							<Button onClick={onApply} variant="contained" color="primary">
								Apply
							</Button>
						</Stack>
					</Stack>
				</Stack>
				<Box sx={{ flex: 1, minHeight: 200, position: 'relative' }}>
					<CustomTabPanel value={tabValue} index={0}>
						{tabValue === 0 && (
							<ConstExpressionInput
								matchingPrimitiveTypes={matchingPrimitiveTypes}
								matchingEnum={parameterEnum}
								valueOptions={valueOptions}
								allowCustomValues={allowCustomValues}
								hasLoadedValueOptionsSource={hasLoadedValueOptionsSource}
								expression={expressionPending}
								onExpressionChange={onExpressionPendingChange}
							/>
						)}
					</CustomTabPanel>
					<CustomTabPanel value={tabValue} index={1}>
						{variableScope && (
							<VarExpressionInput
								matchingPrimitiveTypes={matchingPrimitiveTypes}
								expression={expressionPending as ExpressionModel}
								onExpressionChange={onExpressionPendingChange}
								variableScope={variableScope}
								canCreateVariable={parameterMetadata.expectedExpressionIsAssignable}
								requireCollection={requireCollectionVariable}
							/>
						)}
					</CustomTabPanel>
					<CustomTabPanel value={tabValue} index={2} sx={{ display: tabValue === 2 ? 'flex' : 'none', flexDirection: 'column' }}>
						<ListWithFilter
							items={matchingExpressionMetadataList}
							selectedItemId={(expressionPending as ExpressionModel)?.Type}
							onSelected={onExpressionSelected}
							placeholder="Search expressions"
							getDisplayName={(item) => item.displayName}
							getKey={(item) => item.name}
							getGroup={(item) => item.category}
							emptyMessage="No expressions found"
							icon={(item) => <IconComponent iconUrl={item.iconUrl} iconName={item.iconName} />}
							sx={{ flex: 1, minHeight: 0 }}
						/>
					</CustomTabPanel>
				</Box>
			</Stack>
		</Popover>
	);
}
